import { motion, useReducedMotion } from "framer-motion";

/**
 * Shared substrate for the unauthenticated surfaces (/login, /register) so both
 * read as one system.
 *
 * The photograph is self-hosted from /media rather than pulled off an image
 * CDN: this is the first paint of the product for a buyer, and it should not
 * depend on a third party being up (or on a hotlink that can rot).
 *
 * The only ambient motion is a very slow scale drift on the photograph. It runs
 * for 40s a cycle, is invisible frame to frame, and is disabled outright under
 * prefers-reduced-motion.
 */
export default function AuthBackdrop({ centered = false }) {
  const reduce = useReducedMotion();

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <motion.img
        src="/media/ops-floor.jpg"
        alt=""
        fetchPriority="high"
        decoding="async"
        initial={{ opacity: 0, scale: 1.04 }}
        animate={
          reduce ? { opacity: 0.34, scale: 1.04 } : { opacity: 0.34, scale: [1.04, 1.11] }
        }
        transition={
          reduce
            ? { duration: 0 }
            : {
                opacity: { duration: 1.4, ease: [0.16, 1, 0.3, 1] },
                scale: { duration: 40, repeat: Infinity, repeatType: "reverse", ease: "linear" },
              }
        }
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* Scrims. Two directions so headline copy keeps AAA contrast over the
          photograph at every breakpoint, and the panel edge stays dark. */}
      {/* A left-to-right scrim is only correct when the copy sits on the left.
          A centered card needs an even one, or it lands on the brightest part
          of the photograph. */}
      {centered ? (
        <div className="absolute inset-0 bg-[#09090B]/74" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090B] via-[#09090B]/78 to-[#09090B]/55" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-transparent to-[#09090B]/80" />

      {/* Structure. The grid is the product's own visual language (it is used on
          the dashboard), faded out toward the edges so it reads as engineering,
          not wallpaper. */}
      <div
        className="grid-bg absolute inset-0 opacity-70"
        style={{
          maskImage: "radial-gradient(ellipse 80% 70% at 30% 45%, #000 20%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 30% 45%, #000 20%, transparent 75%)",
        }}
      />

      {/* A single low-saturation brand wash, anchored under the headline. */}
      <div className="absolute -left-40 top-1/3 h-[38rem] w-[38rem] rounded-full bg-blue-600/10 blur-[120px]" />

      {/* Vignette */}
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(ellipse 100% 100% at 50% 50%, transparent 40%, rgba(9,9,11,0.75) 100%)" }}
      />
    </div>
  );
}
